import type { MaybeRefOrGetter } from 'vue';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useDraggable, useWindowSize } from '@vueuse/core';

interface Props {
  handle?: any;
  target: any;
  container?: any;
  placement?: string;
  bottom?: number;
  top?: number;
  left?: number;
  right?: number;
  disabled?: MaybeRefOrGetter<boolean>;
}

function getInitialPosition(
  placement: string,
  top: number,
  bottom: number,
  left: number,
  right: number,
  width: number,
  height: number,
  w: number,
  h: number,
) {
  const x = width - right - w;
  const y = height - bottom - h;
  let res: any = null;
  if (placement === 'br') {
    res = { x, y };
  } else if (placement === 'bl') {
    res = { x: left, y };
  } else if (placement === 'tr') {
    res = { x, y: top };
  } else {
    res = { x: left, y: top };
  }
  return res;
}

const INITIAL_OFFSET = -9999;

function resolveDom(input: any) {
  if (input && typeof input === 'string') {
    return useTemplateRef<HTMLElement>(input);
  }
  return input;
}

function getRect(dom: any) {
  const originalDisplay = dom.style.display;
  const hidden = originalDisplay === 'none';
  if (hidden) {
    dom.style.display = '';
  }
  const rect = dom.getBoundingClientRect();
  if (hidden) {
    dom.style.display = originalDisplay;
  }
  return rect;
}

export function useAdvancedDraggable({
  handle,
  target,
  container,
  placement = 'br',
  bottom: initialBottom = 0,
  top: initialTop = 0,
  left: initialLeft = 0,
  right: initialRight = 0,
  disabled = false,
}: Props) {
  // 记录是否有拖拽位移
  const dragged = ref(false);
  // 开始拖拽的位置
  let dragStartPos = { x: 0, y: 0 };

  const handleRef = resolveDom(handle);
  const targetRef = resolveDom(target);
  const containerRef = resolveDom(container);

  const { width, height } = useWindowSize();

  const { position, isDragging } = useDraggable(targetRef, {
    disabled,
    handle: handleRef,
    containerElement: containerRef,
    axis: 'both',
    initialValue: {
      x: INITIAL_OFFSET,
      y: INITIAL_OFFSET,
    },
    onStart(_, e) {
      dragged.value = false;
      dragStartPos = {
        x: e.clientX,
        y: e.clientY,
      };
    },
    onEnd(_, e) {
      const x = e.clientX;
      const y = e.clientY;
      const l = Math.abs(x - dragStartPos.x) + Math.abs(y - dragStartPos.y);
      if (l > 10) {
        dragged.value = true;
        window.setTimeout(() => {
          dragged.value = false;
        }, 10);
      }
    },
  });

  watch(
    targetRef,
    () => {
      // 初始化时更新一次position
      if (targetRef.value) {
        const rect = getRect(targetRef.value);
        position.value = getInitialPosition(
          placement,
          initialTop,
          initialBottom,
          initialLeft,
          initialRight,
          width.value,
          height.value,
          rect.width,
          rect.height,
        );
      }
    },
    { once: true },
  );

  const style = computed(() => {
    let res = '';
    if (targetRef.value) {
      const rect = getRect(targetRef.value);
      const w = width.value;
      const h = height.value;
      const l = position.value.x;
      const t = position.value.y;
      const r = w - rect.width - l;
      const b = h - rect.height - t;

      if (l < 0 && r < 0) {
        res += `left:0px;`;
      } else if (l < 0 && r >= 0) {
        res += `left:0px;`;
      } else if (l >= 0 && r < 0) {
        if (l + r < 0) {
          res += `left:0px;`;
        } else {
          res += `right:0px;`;
        }
      } else {
        if (l < r) {
          res += `left:${l}px;`;
        } else {
          res += `right:${r}px;`;
        }
      }
      if (t < 0 && b < 0) {
        res += `top:0px;`;
      } else if (t < 0 && b >= 0) {
        res += `top:0px;`;
      } else if (t >= 0 && b < 0) {
        if (t + b < 0) {
          res += `top:0px;`;
        } else {
          res += `bottom:0px;`;
        }
      } else {
        if (t < b) {
          res += `top:${t}px;`;
        } else {
          res += `bottom:${b}px;`;
        }
      }
    }
    return res;
  });

  return { isDragging, dragged, style };
}
